import React, { useEffect, useState } from "react";
import { useGetCreditReceiptsQuery } from "../../../redux/api/modules/billingApi";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import CustomTable from "../../../shared/CustomTable";
import CustomPagination from "../../../shared/CustomPagination";
import { CreditReceiptColumns } from "../../../constants/BillingConstants";
import { PrintIcon } from "../../../utils/TableActionIcons";
import BillingFilters from "../../../components/his/billing/BillingFilters";
import usePersistentBillingFilters from "../../../hooks/usePersistentBillingFilters";
import { useSelector } from 'react-redux';
import { hasBillingSearchFilters, omitBillingDatesForSkipInitialLoad, shouldSkipInitialBillingLoad } from '../../../utils/businessLevelAccess';

function CreditReceipts() {
    const authUser = useSelector(state => state?.auth?.user);
    const skipInitialBillingLoad = shouldSkipInitialBillingLoad(authUser);
    const [payload, setPayload, resetPayload, appliedPayload, applyFilters] = usePersistentBillingFilters('creditReceipt', { per_page: 20 });

    const queryPayload = React.useMemo(() => ({
        patient_name: appliedPayload.query || "",
        file_number: appliedPayload.file_number || appliedPayload.filters?.file_number || "",
        document_id: appliedPayload.document_id || appliedPayload.filters?.document_id || "",
        receipt_number: appliedPayload.filters?.receipt_number || appliedPayload.filters?.invoice_number || "",
        department_id: appliedPayload.filters?.department_id || "",
        start_date: appliedPayload.start_date,
        end_date: appliedPayload.end_date,
        page: appliedPayload.page,
        per_page: appliedPayload.per_page
    }), [appliedPayload]);

    const {
        data,
        isLoading: queryLoading,
        isFetching: queryFetching,
        refetch
    } = useGetCreditReceiptsQuery(queryPayload, {
        skip: skipInitialBillingLoad && !hasBillingSearchFilters(appliedPayload)
    });

    const loading = queryLoading || queryFetching;

    const [searchQuery, setSearchQuery] = useState(payload?.query || "");
    const navigate = useNavigate();
    const { t } = useTranslation();

    const fetchInitialData = () => {
        refetch();
    };

    

    const onPageChange = async (newPage, newRowsPerPage) => {
        setPayload((prevPayload) => ({
            ...prevPayload,
            page: newPage,
            per_page: newRowsPerPage,
        }));
    };

    const actionBodyTempelate = (rowData) => {
        return (
            <div className="flex gap-2 justify-center">
                <PrintIcon
                    title={t("Print Receipt")}
                    iconSize="20"
                    onClick={() =>
                        navigate(`/print-invoice/${rowData?.receipt_number}`, {
                            state: {
                                invoiceNumber: rowData?.receipt_number,
                                activeTabIndex: 5,
                                activeChildTabIndex: 0,
                                creditReceipt: true
                            },
                        })
                    }
                />
            </div>
        );
    };

    const actions = {
        header: t("Actions"),
        body: actionBodyTempelate,
        headerCellClass: 'flex items-center justify-center'
    };

    const columnsData = [...CreditReceiptColumns(t)];

    return (
        <div className="">
            <BillingFilters
                applyFilters={applyFilters}
                setSearchQuery={setSearchQuery}
                fetchInitialData={fetchInitialData}
                searchQuery={searchQuery}
                payloadData={payload}
                setPayloadData={setPayload}
                refetch={refetch}
                invoiceType="creditReceipt"
                resetFilters={() => {
                    resetPayload();
                    setSearchQuery("");
                }}
            />
            <div>
                <CustomTable
                    isLoading={loading}
                    data={data?.data || []}
                    columns={columnsData}
                    rowClassName={row => row?.is_cancelled ? '!bg-red-100 !text-red-900' : ''}
                />
                {data?.data?.length > 0 && !loading && (
                    <CustomPagination
                        currentPage={data?.page || payload?.page || 1}
                        rowsPerPage={data?.per_page || payload?.per_page || 20}
                        totalRecords={data?.total_records || data?.totalRecords || 0}
                        totalPages={data?.total_pages || data?.totalPages || 1}
                        onPageChange={onPageChange}
                    />
                )}
            </div>
        </div>
    );
}

export default CreditReceipts;
